import Decimal from "decimal.js";

/**
 * The formula language of the Results → Upload layout editor: arithmetic over
 * the columns of a supplier's input workbook, evaluated in exact decimal so a
 * value sitting on a rounding boundary is not flipped by float noise.
 *
 *   formula  := sum
 *   sum      := product (("+" | "-") product)*
 *   product  := unary (("*" | "/") unary)*
 *   unary    := "-" unary | primary
 *   primary  := number | "[" header "]" | "(" sum ")"
 *             | "DEFAULT(" "[" header "]" "," number ")"
 *
 * `[SAP COST]` reads that header's cell in the current row; a blank or
 * non-numeric cell fails the row, except inside `DEFAULT([header], n)`, which
 * reads a blank cell as `n`.
 *
 * Shared so the server can refuse a layout whose formula does not parse, and
 * the browser can evaluate the same syntax.
 */

type Node =
  | { kind: "number"; value: Decimal }
  | { kind: "ref"; header: string }
  | { kind: "default"; header: string; fallback: Decimal }
  | { kind: "negate"; operand: Node }
  | { kind: "binary"; op: "+" | "-" | "*" | "/"; left: Node; right: Node };

export interface UploadFormula {
  root: Node;
  /** Every header the formula reads, in first-use order. */
  headers: string[];
}

export type ParseUploadFormulaResult =
  { ok: true; formula: UploadFormula } | { ok: false; error: string };

/** A cell as the formula sees it: raw text or number, or nothing. */
export type UploadFormulaCell = string | number | null | undefined;

export type EvaluateUploadFormulaResult =
  | { ok: true; value: Decimal }
  | { ok: false; reason: "blank" | "not-a-number"; header: string }
  | { ok: false; reason: "divide-by-zero" };

type Token =
  | { kind: "number"; text: string }
  | { kind: "header"; name: string }
  | { kind: "word"; text: string }
  | { kind: "symbol"; text: "+" | "-" | "*" | "/" | "(" | ")" | "," };

class FormulaSyntaxError extends Error {}

const DEFAULT_USAGE = "DEFAULT takes ([header], number)";

const SYMBOLS = new Set(["+", "-", "*", "/", "(", ")", ","]);

function isSymbol(
  char: string,
): char is "+" | "-" | "*" | "/" | "(" | ")" | "," {
  return SYMBOLS.has(char);
}

function readHeader(source: string, start: number): [Token, number] {
  const end = source.indexOf("]", start + 1);
  if (end === -1) throw new FormulaSyntaxError("Missing ] after a header name");
  const name = source.slice(start + 1, end).trim();
  if (name === "")
    throw new FormulaSyntaxError("Empty [] — put a header name inside");
  return [{ kind: "header", name }, end + 1];
}

function readRun(source: string, start: number, pattern: RegExp): number {
  let end = start;
  while (end < source.length && pattern.test(source.charAt(end))) end += 1;
  return end;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const char = source.charAt(i);
    if (/\s/.test(char)) {
      i += 1;
    } else if (char === "[") {
      const [token, next] = readHeader(source, i);
      tokens.push(token);
      i = next;
    } else if (/[0-9.]/.test(char)) {
      const end = readRun(source, i, /[0-9.]/);
      tokens.push({ kind: "number", text: source.slice(i, end) });
      i = end;
    } else if (/[A-Za-z]/.test(char)) {
      const end = readRun(source, i, /[A-Za-z]/);
      tokens.push({ kind: "word", text: source.slice(i, end) });
      i = end;
    } else if (isSymbol(char)) {
      tokens.push({ kind: "symbol", text: char });
      i += 1;
    } else {
      throw new FormulaSyntaxError(`Unexpected character "${char}"`);
    }
  }
  return tokens;
}

function describe(token: Token | undefined): string {
  if (!token) return "the end of the formula";
  if (token.kind === "header") return `[${token.name}]`;
  return `"${token.kind === "number" || token.kind === "word" ? token.text : token.text}"`;
}

function toDecimal(text: string): Decimal {
  if (!/^(\d+\.?\d*|\.\d+)$/.test(text)) {
    throw new FormulaSyntaxError(`"${text}" is not a number`);
  }
  return new Decimal(text);
}

/** Recursive-descent parser over the token list. */
class Parser {
  private index = 0;
  readonly headers: string[] = [];

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private next(): Token | undefined {
    const token = this.tokens[this.index];
    this.index += 1;
    return token;
  }

  private isSymbol(text: string): boolean {
    const token = this.peek();
    return token?.kind === "symbol" && token.text === text;
  }

  private expectSymbol(text: string, usage?: string): void {
    if (!this.isSymbol(text)) {
      const found = `Expected "${text}" but found ${describe(this.peek())}`;
      throw new FormulaSyntaxError(usage ? `${usage}: ${found}` : found);
    }
    this.index += 1;
  }

  private addHeader(name: string): void {
    if (!this.headers.includes(name)) this.headers.push(name);
  }

  parseFormula(): Node {
    if (this.tokens.length === 0)
      throw new FormulaSyntaxError("The formula is empty");
    const root = this.parseSum();
    if (this.peek()) {
      throw new FormulaSyntaxError(`Unexpected ${describe(this.peek())}`);
    }
    return root;
  }

  private parseSum(): Node {
    let left = this.parseProduct();
    while (this.isSymbol("+") || this.isSymbol("-")) {
      const op = this.isSymbol("+") ? "+" : "-";
      this.index += 1;
      left = { kind: "binary", op, left, right: this.parseProduct() };
    }
    return left;
  }

  private parseProduct(): Node {
    let left = this.parseUnary();
    while (this.isSymbol("*") || this.isSymbol("/")) {
      const op = this.isSymbol("*") ? "*" : "/";
      this.index += 1;
      left = { kind: "binary", op, left, right: this.parseUnary() };
    }
    return left;
  }

  private parseUnary(): Node {
    if (this.isSymbol("-")) {
      this.index += 1;
      return { kind: "negate", operand: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const token = this.next();
    if (!token) throw this.expectedOperand(token);
    if (token.kind === "number") {
      return { kind: "number", value: toDecimal(token.text) };
    }
    if (token.kind === "header") return this.parseRef(token.name);
    if (token.kind === "word") return this.parseFunction(token.text);
    return this.parseGroup(token);
  }

  private parseRef(header: string): Node {
    this.addHeader(header);
    return { kind: "ref", header };
  }

  /** `( sum )`; any other symbol here is out of place. */
  private parseGroup(token: Token): Node {
    if (token.kind !== "symbol" || token.text !== "(") {
      throw this.expectedOperand(token);
    }
    const inner = this.parseSum();
    this.expectSymbol(")");
    return inner;
  }

  private expectedOperand(token: Token | undefined): FormulaSyntaxError {
    return new FormulaSyntaxError(
      `Expected a number, a [header] or "(" but found ${describe(token)}`,
    );
  }

  /** The only function: DEFAULT([header], number). */
  private parseFunction(name: string): Node {
    if (name.toUpperCase() !== "DEFAULT") {
      throw new FormulaSyntaxError(
        `Unknown function "${name}" — only DEFAULT([header], number) exists`,
      );
    }
    this.expectSymbol("(", DEFAULT_USAGE);
    const ref = this.next();
    if (ref?.kind !== "header") {
      throw new FormulaSyntaxError(
        "DEFAULT needs a [header] as its first argument",
      );
    }
    this.expectSymbol(",", DEFAULT_USAGE);
    const negative = this.isSymbol("-");
    if (negative) this.index += 1;
    const fallback = this.next();
    if (fallback?.kind !== "number") {
      throw new FormulaSyntaxError(
        "DEFAULT needs a number as its second argument",
      );
    }
    this.expectSymbol(")", DEFAULT_USAGE);
    this.addHeader(ref.name);
    const value = toDecimal(fallback.text);
    return {
      kind: "default",
      header: ref.name,
      fallback: negative ? value.negated() : value,
    };
  }
}

export function parseUploadFormula(source: string): ParseUploadFormulaResult {
  try {
    const parser = new Parser(tokenize(source));
    const root = parser.parseFormula();
    return { ok: true, formula: { root, headers: parser.headers } };
  } catch (error) {
    if (error instanceof FormulaSyntaxError)
      return { ok: false, error: error.message };
    throw error;
  }
}

class EvaluationFailure extends Error {
  constructor(
    readonly result: Exclude<EvaluateUploadFormulaResult, { ok: true }>,
  ) {
    super(result.reason);
  }
}

function cellText(cell: UploadFormulaCell): string {
  if (cell === null || cell === undefined) return "";
  return String(cell).trim();
}

function readNumber(header: string, cell: UploadFormulaCell): Decimal {
  if (typeof cell === "number" && Number.isFinite(cell))
    return new Decimal(cell);
  const text = cellText(cell);
  if (text === "")
    throw new EvaluationFailure({ ok: false, reason: "blank", header });
  // Plain decimals only: Decimal.js would also take NaN, Infinity, 0x1F, 1e3.
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(text)) {
    throw new EvaluationFailure({ ok: false, reason: "not-a-number", header });
  }
  return new Decimal(text);
}

function applyBinary(
  op: "+" | "-" | "*" | "/",
  left: Decimal,
  right: Decimal,
): Decimal {
  switch (op) {
    case "+":
      return left.plus(right);
    case "-":
      return left.minus(right);
    case "*":
      return left.times(right);
    case "/":
      if (right.isZero())
        throw new EvaluationFailure({ ok: false, reason: "divide-by-zero" });
      return left.div(right);
  }
}

function evaluate(
  node: Node,
  cell: (header: string) => UploadFormulaCell,
): Decimal {
  switch (node.kind) {
    case "number":
      return node.value;
    case "ref":
      return readNumber(node.header, cell(node.header));
    case "default":
      return cellText(cell(node.header)) === ""
        ? node.fallback
        : readNumber(node.header, cell(node.header));
    case "negate":
      return evaluate(node.operand, cell).negated();
    case "binary":
      return applyBinary(
        node.op,
        evaluate(node.left, cell),
        evaluate(node.right, cell),
      );
  }
}

export function evaluateUploadFormula(
  formula: UploadFormula,
  cell: (header: string) => UploadFormulaCell,
): EvaluateUploadFormulaResult {
  try {
    return { ok: true, value: evaluate(formula.root, cell) };
  } catch (error) {
    if (error instanceof EvaluationFailure) return error.result;
    throw error;
  }
}
